// What shape of deck the assistant is being asked to write.
//
// The app renders a deck two ways, and they want different writing:
//
//   * **canvas** — a fixed 16:9 stage per slide, every block positioned by a
//     `frame`, exporting 1:1 to PowerPoint. A slide holds one idea.
//   * **flow** — blocks stacked down a scrolling page, no positioning, as long
//     as the content needs.
//
// The prompt used to describe neither, so an assistant wrote for the flow
// renderer by default and the result needed rearranging before it would
// present. The style chosen on the AI Prompt page now travels into the prompt
// *and* into the starter deck, so both agree.
//
// Frames are percentages of the slide's content region — the area below the
// header bar. See utils/slideFrames.js, which owns the geometry these numbers
// are in.

import { DECK_FORMAT, DECK_SCHEMA_TYPE, DECK_VERSION } from './deckContractConstants';

/** Frame the headline of a canvas slide sits in. */
export const HEADLINE_FRAME = { x: 6, y: 6, w: 88, h: 14 };

/** The region below it that everything else shares. */
export const BODY_REGION = { x: 6, top: 24, bottom: 92, w: 88 };

/** Gap between two stacked body blocks, in the same percentage units. */
const BODY_GAP = 2;

const round = (value) => Math.round(value * 10) / 10;

/**
 * Give every block on a canvas slide a frame: the headline across the top, and
 * whatever follows sharing the space beneath it.
 *
 * Without this the renderer falls back to a cascade layout meant for blocks a
 * person is about to drag, and a generated deck opens with its blocks piled on
 * top of each other. A block that already carries a frame keeps it.
 */
export const applyCanvasFrames = (blocks = []) => {
  const bySlide = new Map();
  blocks.forEach((block) => {
    const slideId = block && block.slideId;
    if (!bySlide.has(slideId)) bySlide.set(slideId, []);
    bySlide.get(slideId).push(block);
  });

  const frames = new Map();
  bySlide.forEach((group) => {
    const first = group[0];
    const hasHeadline = first && (first.type === 'headline' || first.type === 'heading');
    if (hasHeadline) frames.set(first, { ...HEADLINE_FRAME });

    const body = hasHeadline ? group.slice(1) : group;
    if (!body.length) return;

    const top = hasHeadline ? BODY_REGION.top : HEADLINE_FRAME.y;
    const available = BODY_REGION.bottom - top;
    const height = (available - BODY_GAP * (body.length - 1)) / body.length;
    body.forEach((block, index) => {
      frames.set(block, {
        x: BODY_REGION.x,
        y: round(top + index * (height + BODY_GAP)),
        w: BODY_REGION.w,
        h: round(height)
      });
    });
  });

  return blocks.map((block) => (
    block && !block.frame && frames.has(block) ? { ...block, frame: frames.get(block) } : block
  ));
};

/** A divider block, which is what actually ends a slide. */
export const slideBreakBlock = (slideId) => ({
  type: 'divider',
  slideId,
  content: {
    style: 'solid',
    thickness: 2,
    color: '#e5e7eb',
    spacing: 'medium',
    width: 'full',
    isSlideBreak: true,
    slideId
  }
});

/**
 * Put a slide break between each run of blocks that share a `slideId`.
 *
 * A `slideId` names a slide; a divider flagged `isSlideBreak` is what ends
 * one. A deck without the dividers opens with every block in a section piled
 * onto one slide, so the deck this app writes carries them — and so does the
 * example handed to an assistant, which is the shape it will copy.
 */
export const withSlideBreaks = (blocks = []) => {
  const result = [];
  let current = null;
  blocks.forEach((block) => {
    const slideId = block && block.slideId;
    if (slideId && current !== null && slideId !== current) result.push(slideBreakBlock(slideId));
    if (slideId) current = slideId;
    result.push(block);
  });
  return result;
};

/**
 * The styles offered on the AI Prompt page.
 *
 * PowerPoint is first and is the default: the app's own default template is
 * the 16:9 one, and a deck written for it exports to PowerPoint without being
 * rebuilt.
 */
export const DECK_STYLES = [
  {
    id: 'powerpoint',
    name: 'PowerPoint (16:9)',
    designMode: 'canvas',
    summary: 'Fixed 16:9 slides, one idea each — exports to PowerPoint as laid out.'
  },
  {
    id: 'flow',
    name: 'Scrolling lesson page',
    designMode: 'flow',
    summary: 'Blocks stacked down the page, as long as the content needs.'
  },
  {
    id: 'custom',
    name: 'Match my current template',
    designMode: null,
    summary: 'Follow the deck already open in the Slides tab.'
  }
];

export const DEFAULT_DECK_STYLE = 'powerpoint';

export const getDeckStyle = (id) =>
  DECK_STYLES.find((style) => style.id === id) || DECK_STYLES.find((style) => style.id === DEFAULT_DECK_STYLE);

/** Blocks actually present in a loaded deck, for describing it to an assistant. */
const deckBlockTypes = (deck) => {
  const types = new Set();
  const sections = deck && deck.sections;
  const list = Array.isArray(sections) ? sections : Object.values(sections || {});
  list.forEach((section) => {
    const blocks = Array.isArray(section) ? section : (section && section.blocks) || [];
    blocks.forEach((block) => {
      if (block && typeof block.type === 'string') types.add(block.type);
    });
  });
  return [...types];
};

const deckSectionTitles = (deck) => {
  const sections = deck && deck.sections;
  const list = Array.isArray(sections) ? sections : Object.values(sections || {});
  return list
    .map((section) => (Array.isArray(section) ? '' : (section && section.title) || ''))
    .filter(Boolean);
};

/**
 * The design mode a loaded deck is in. A deck whose blocks carry frames is a
 * canvas deck even if it never said so — that is what the frames are for.
 */
export const deckDesignMode = (deck) => {
  if (!deck || typeof deck !== 'object') return null;
  if (deck.slideDesignMode === 'canvas' || deck.slideDesignMode === 'flow') return deck.slideDesignMode;
  const sections = Array.isArray(deck.sections) ? deck.sections : Object.values(deck.sections || {});
  const framed = sections.some((section) => {
    const blocks = Array.isArray(section) ? section : (section && section.blocks) || [];
    return blocks.some((block) => block && block.frame);
  });
  // Most templates never write the key — the renderer treats its absence as
  // flow — so a deck that is not framed is a flow deck. Only "there is no
  // deck" answers null.
  return framed ? 'canvas' : 'flow';
};

/** Whether there is a deck here at all, rather than a placeholder object. */
const hasContent = (deck) => {
  if (!deck || typeof deck !== 'object') return false;
  const sections = Array.isArray(deck.sections) ? deck.sections : Object.values(deck.sections || {});
  return sections.length > 0;
};

/**
 * Describe the deck the instructor already has open, so a new one can be
 * written to match it. This is what "Match my current template" is: a template
 * picked or uploaded on the launch page is the deck in the Slides tab, and the
 * things worth matching are its layout, its sections and the blocks it uses.
 */
export const describeCustomDeck = (deck) => {
  if (!hasContent(deck)) return '';
  const lines = [];
  const mode = deckDesignMode(deck);
  if (mode === 'canvas') {
    lines.push('- Layout: fixed 16:9 slides with every block positioned by a `frame` (see below).');
  } else if (mode === 'flow') {
    lines.push('- Layout: blocks stacked down a scrolling page, with no `frame` on any block.');
  }

  const name = typeof deck.name === 'string' ? deck.name : (deck.slideDeckName || '');
  if (name) lines.push(`- The template is called "${name}".`);

  const titles = deckSectionTitles(deck);
  if (titles.length) lines.push(`- Its sections are titled: ${titles.join(', ')}.`);

  const types = deckBlockTypes(deck);
  if (types.length) {
    lines.push(`- It is built from these block types, so prefer them: ${types.join(', ')}.`);
  }

  const titleCard = deck.titleCardSettings;
  if (titleCard && titleCard.enabled !== false && (titleCard.title || titleCard.subtitle)) {
    lines.push('- It opens with a title card, so the first section is the lesson itself rather than a title slide.');
  }

  if (!lines.length) return '';
  return `The instructor has a template open in the app and wants this deck to match it:\n${lines.join('\n')}`;
};

const CANVAS_RULES = `This deck is for **16:9 slides that export to PowerPoint**, so:

- Set \`"slideDesignMode": "canvas"\` at the top level of the deck, beside \`courseTopic\`.
- Give every block a \`frame\`: \`{ "x": 6, "y": 24, "w": 88, "h": 66 }\`, in percentages of
  the slide area below the header bar. Use these unless a slide needs otherwise:
  - the headline: \`{ "x": 6, "y": 6, "w": 88, "h": 14 }\`
  - one block under it: \`{ "x": 6, "y": 24, "w": 88, "h": 66 }\`
  - two side by side: \`{ "x": 6, "y": 24, "w": 43, "h": 66 }\` and \`{ "x": 51, "y": 24, "w": 43, "h": 66 }\`
  - two stacked: \`{ "x": 6, "y": 24, "w": 88, "h": 32 }\` and \`{ "x": 6, "y": 58, "w": 88, "h": 32 }\`
- Frames must not overlap, and must stay inside 0–100 on both axes.
- **One idea per slide.** A 16:9 slide holds a headline plus about four bullets, or
  one picture with its caption — no more. Give a second point its own slide rather
  than a fuller one; a slide that overflows is scaled down until it cannot be read
  from the back of the room.
- Two or three blocks on a slide is the most that lays out well.`;

const FLOW_RULES = `This deck is a **scrolling lesson page**, so:

- Leave \`slideDesignMode\` out, and do not put a \`frame\` on any block — blocks
  stack down the page in the order you write them.
- A section can run as long as its content needs; the reader scrolls.
- Group related blocks under one \`slideId\` freely, since nothing has to fit a
  fixed stage.`;

/**
 * The layout half of the contract: what shape of deck to write. Appended to
 * the schema contract, which describes the block types themselves.
 */
export const deckStyleContractText = (styleId, customDeck) => {
  const style = getDeckStyle(styleId);

  if (style.id === 'custom') {
    const description = describeCustomDeck(customDeck);
    // Nothing open to match: the PowerPoint rules are the app's default and a
    // better answer than no layout guidance at all.
    if (!description) {
      return `### Slide layout — PowerPoint (16:9)\n\n${CANVAS_RULES}`;
    }
    const mode = deckDesignMode(customDeck);
    const rules = mode === 'flow' ? FLOW_RULES : CANVAS_RULES;
    return `### Slide layout — match the instructor's template\n\n${description}\n\n${rules}`;
  }

  return `### Slide layout — ${style.name}\n\n${style.designMode === 'canvas' ? CANVAS_RULES : FLOW_RULES}`;
};

/**
 * A tiny deck in the chosen style, handed to an assistant as the shape to copy.
 *
 * Two slides in one section is enough to show everything that matters: the
 * top-level keys, a slide's blocks, the divider that ends a slide, and — in
 * the 16:9 style — the frames. Rules in prose get skimmed; a file gets
 * imitated.
 */
export const buildExampleDeck = (styleId, customDeck) => {
  const canvas = deckStyleDesignMode(styleId, customDeck) === 'canvas';
  const frame = (value) => (canvas ? { frame: value } : {});

  const blocks = [
    { type: 'headline', slideId: 'bridge-in-1', content: '<h2>The first slide title</h2>', ...frame(HEADLINE_FRAME) },
    { type: 'text', slideId: 'bridge-in-1', content: '<p>One idea, written to the student.</p>', ...frame({ x: 6, y: 24, w: 88, h: 66 }) },
    slideBreakBlock('bridge-in-2'),
    { type: 'headline', slideId: 'bridge-in-2', content: '<h2>The second slide title</h2>', ...frame(HEADLINE_FRAME) },
    { type: 'list', slideId: 'bridge-in-2', content: '<ul><li>A point</li><li>Another point</li></ul>', ...frame({ x: 6, y: 24, w: 88, h: 66 }) }
  ];

  return {
    format: DECK_FORMAT,
    version: DECK_VERSION,
    schema_type: DECK_SCHEMA_TYPE,
    ...(canvas ? { slideDesignMode: 'canvas' } : {}),
    courseTopic: 'Example — replace every string in this file',
    _readme: canvas
      ? 'Two slides in one section, in the 16:9 PowerPoint layout. Every block carries a frame, and the divider with isSlideBreak is what ends a slide — without it every block lands on the same slide. Copy this shape; write the real lesson into it.'
      : 'Two slides in one section, in the scrolling page layout. No frames anywhere; the divider with isSlideBreak is what ends a slide. Copy this shape; write the real lesson into it.',
    sections: {
      'bridge-in': { id: 'bridge-in', title: 'Bridge-In', blocks }
    }
  };
};

/**
 * The design mode the starter deck should be built in, so "Download starter
 * deck" and the prompt do not disagree about what is being made.
 */
export const deckStyleDesignMode = (styleId, customDeck) => {
  const style = getDeckStyle(styleId);
  if (style.id === 'custom') return deckDesignMode(customDeck) || 'canvas';
  return style.designMode;
};
